import type { ReactNode } from "react";
import {
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";

import { ConfirmDialog } from "@/atoms";
import { ConfirmContext } from "@/hooks/shared/use-confirm"; // eslint-disable-line code-style/absolute-imports-only -- internal hooks import
import type { ConfirmContextValueInterface, ConfirmOptionsInterface } from "@/interfaces";

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
    const resolverRef = useRef<((value: boolean) => void) | null>(null);

    const [options, setOptions] = useState<ConfirmOptionsInterface | null>(null);

    const [isOpen, setIsOpen] = useState(false);

    const askHandler = useCallback(
        (input: ConfirmOptionsInterface) => new Promise<boolean>((resolve) => {
            resolverRef.current = resolve;

            setOptions(input);

            setIsOpen(true);
        }),
        [],
    );

    const closeWithHandler = useCallback(
        (result: boolean) => {
            setIsOpen(false);

            const resolver = resolverRef.current;

            resolverRef.current = null;

            if (resolver) resolver(result);
        },
        [],
    );

    const cancelHandler = useCallback(
        () => closeWithHandler(false),
        [closeWithHandler],
    );

    const confirmHandler = useCallback(
        () => closeWithHandler(true),
        [closeWithHandler],
    );

    const value = useMemo<ConfirmContextValueInterface>(
        () => ({ onAsk: askHandler }),
        [askHandler],
    );

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            <ConfirmDialog
                isOpen={isOpen}
                options={options}
                onCancel={cancelHandler}
                onConfirm={confirmHandler}
            />
        </ConfirmContext.Provider>
    );
};
